import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, setDoc, where, serverTimestamp, addDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { isReviewable } from '../../utils/customerFlow';
import LoadingState from '../shared/LoadingState';
import './CustomerApp.css';

const CustomerApp = () => {
  const { companySlug } = useParams();
  const [company, setCompany] = useState(null);
  const [services, setServices] = useState([]);
  const [reservations, setReservations] = useState([]);

  const normalizeCompany = (data) => ({
    name: data?.name || data?.companyName || data?.company_name || '',
    logoUrl: data?.logoUrl || data?.logo || data?.logoURL || data?.logo_url || '',
    primaryColor: data?.primaryColor || data?.primaryColour || data?.primary_color || '#2563eb',
    secondaryColor: data?.secondaryColor || data?.secondaryColour || data?.secondary_color || '#7c3aed',
    slug: data?.slug || data?.companySlug || data?.company_slug || '',
    phone: data?.phone || data?.whatsapp || data?.contactPhone || data?.contactWhatsApp || ''
  });
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', address: '' });
  const [selectedService, setSelectedService] = useState(null);
  const [reservationDetails, setReservationDetails] = useState({ date: '', time: '', checkIn: '', checkOut: '', guests: 1, notes: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [activePage, setActivePage] = useState('dashboard');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const profileRef = doc(db, 'customerProfiles', currentUser.uid);
          const profileSnap = await getDoc(profileRef);
          if (profileSnap.exists()) {
            const data = profileSnap.data();
            setProfile({
              name: data.name || '',
              email: data.email || currentUser.email || '',
              phone: data.phone || '',
              address: data.address || ''
            });
          } else {
            setProfile({ name: currentUser.displayName || '', email: currentUser.email || '', phone: '', address: '' });
          }
        } catch (error) {
          if (error?.code !== 'permission-denied') {
            setMessage('Não foi possível carregar seu perfil neste momento.');
          }
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadCompany = async () => {
      if (!companySlug) return;
      try {
        const companiesRef = collection(db, 'companies');
        const q = query(companiesRef, where('slug', '==', companySlug));
        let snap = await getDocs(q);
        let companyDoc = null;

        if (!snap.empty) {
          companyDoc = snap.docs[0];
        } else {
          const fallbackDoc = await getDoc(doc(db, 'companies', companySlug));
          if (fallbackDoc.exists()) {
            companyDoc = fallbackDoc;
          }
        }

        if (!companyDoc) {
          const nameFallbackQuery = query(companiesRef, where('name', '==', companySlug));
          const nameFallbackSnap = await getDocs(nameFallbackQuery);
          if (!nameFallbackSnap.empty) {
            companyDoc = nameFallbackSnap.docs[0];
          }
        }

        if (!companyDoc) {
          const companyNameFallbackQuery = query(companiesRef, where('companyName', '==', companySlug));
          const companyNameFallbackSnap = await getDocs(companyNameFallbackQuery);
          if (!companyNameFallbackSnap.empty) {
            companyDoc = companyNameFallbackSnap.docs[0];
          }
        }

        if (!companyDoc) {
          setMessage('Empresa não encontrada. Verifique o link ou fale com a recepção.');
          return;
        }

        const normalizedCompany = normalizeCompany(companyDoc.data());
        setCompany({ id: companyDoc.id, ...normalizedCompany });
        let servicesSnap = await getDocs(collection(db, `companies/${companyDoc.id}/services`));
        if (servicesSnap.empty) {
          servicesSnap = await getDocs(collection(db, `companies/${companySlug}/services`));
        }
        setServices(servicesSnap.docs.map(doc => ({
          id: doc.id,
          name: doc.data()?.name || doc.data()?.serviceName || doc.data()?.title || 'Serviço',
          description: doc.data()?.description || doc.data()?.details || '',
          category: doc.data()?.category || doc.data()?.type || 'Serviço',
          price: doc.data()?.price ?? doc.data()?.value ?? 0,
          ...doc.data()
        })));
      } catch (error) {
        console.error('Erro ao carregar empresa:', error);
        setMessage('Não foi possível carregar os dados da empresa neste momento.');
      }
    };

    loadCompany();
  }, [companySlug]);

  useEffect(() => {
    const loadReservations = async () => {
      if (!user?.uid || !company?.id) return;
      try {
        const reservationsRef = collection(db, `companies/${company.id}/reservations`);
        const q = query(reservationsRef, where('customerUid', '==', user.uid));
        const snap = await getDocs(q);
        setReservations(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        setReservations([]);
      }
    };

    loadReservations();
  }, [user, company]);

  const filteredServices = useMemo(() => {
    const term = search.toLowerCase();
    return services.filter(service => {
      const matchesSearch = !term || service.name?.toLowerCase().includes(term) || service.description?.toLowerCase().includes(term);
      const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [services, search, selectedCategory]);

  const serviceCategories = useMemo(() => {
    const categories = ['all'];
    services.forEach(service => {
      if (service.category) categories.push(service.category);
    });
    return [...new Set(categories)];
  }, [services]);

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      setMessage('Login com Google concluído.');
    } catch (error) {
      setMessage(error.message || 'Erro ao fazer login.');
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!user?.uid) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'customerProfiles', user.uid), {
        uid: user.uid,
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        address: profile.address,
        companyId: company?.id || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      }, { merge: true });
      setMessage('Perfil concluído com sucesso.');
    } catch (error) {
      setMessage('Erro ao salvar perfil.');
    } finally {
      setSaving(false);
    }
  };

  const isPousadaService = (service) => {
    if (!service) return false;
    const type = String(service.category || service.type || service.name || '').toLowerCase();
    return service.isPousada || type.includes('pousada');
  };

  const getServicePrice = (service) => {
    const value = Number(service?.price ?? service?.value ?? 0);
    return Number.isFinite(value) ? value : 0;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(value) || 0);
  };

  const selectServiceForBooking = (service) => {
    setSelectedService(service);
    setReservationDetails({ date: '', time: '', checkIn: '', checkOut: '', guests: 1, notes: '' });
    setMessage('');
    setActivePage('booking');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReserve = async (service) => {
    if (!user?.uid || !company?.id) return;
    if (!profile.name || !profile.email || !profile.phone || !profile.address) {
      setMessage('Preencha seus dados de contato para confirmar o pedido.');
      return;
    }

    const isPousada = isPousadaService(service);
    if (isPousada && (!reservationDetails.checkIn || !reservationDetails.checkOut)) {
      setMessage('Informe data de check-in e check-out para este serviço.');
      return;
    }
    if (isPousada && reservationDetails.checkIn > reservationDetails.checkOut) {
      setMessage('A data de check-out deve ser igual ou posterior à data de check-in.');
      return;
    }
    if (!isPousada && (!reservationDetails.date || !reservationDetails.time)) {
      setMessage('Informe a data e o horário desejados para a reserva.');
      return;
    }

    setSaving(true);
    try {
      await setDoc(doc(db, 'customerProfiles', user.uid), {
        uid: user.uid,
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        address: profile.address,
        companyId: company.id,
        updatedAt: serverTimestamp()
      }, { merge: true });

      const dateTime = isPousada
        ? new Date(`${reservationDetails.checkIn}T12:00`)
        : new Date(`${reservationDetails.date}T${reservationDetails.time}`);

      const reservationData = {
        customerUid: user.uid,
        clientName: profile.name,
        clientEmail: profile.email,
        clientPhone: profile.phone,
        address: profile.address,
        serviceId: service.id,
        serviceName: service.name || service.title || 'Serviço',
        companyId: company.id,
        companyName: company.name,
        requestedDate: reservationDetails.date,
        requestedTime: reservationDetails.time,
        date: dateTime,
        participants: Number(reservationDetails.guests) || 1,
        guests: Number(reservationDetails.guests) || 1,
        notes: reservationDetails.notes,
        status: 'pending',
        paymentStatus: 'unpaid',
        totalAmount: getServicePrice(service),
        paidAmount: 0,
        createdBy: profile.name,
        createdByEmail: profile.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (isPousada) {
        const checkInDate = new Date(`${reservationDetails.checkIn}T12:00`);
        const checkOutDate = new Date(`${reservationDetails.checkOut}T12:00`);
        const dailyCount = Math.max(1, Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24)));

        reservationData.checkIn = reservationDetails.checkIn;
        reservationData.checkOut = reservationDetails.checkOut;
        reservationData.checkInDate = checkInDate;
        reservationData.checkOutDate = checkOutDate;
        reservationData.dailyCount = dailyCount;
      }

      const reservationRef = await addDoc(collection(db, `companies/${company.id}/reservations`), reservationData);
      setReservations(prev => [{ id: reservationRef.id, ...reservationData }, ...prev]);
      setReservationDetails({ date: '', time: '', checkIn: '', checkOut: '', guests: 1, notes: '' });
      setSelectedService(null);
      setActivePage('reservations');
      setMessage(`Pedido enviado com sucesso! Número: ${reservationRef.id}`);

      if (company.phone) {
        const waLink = `https://wa.me/${company.phone.replace(/\D/g, '')}?text=${encodeURIComponent(`Olá ${company.name || 'empresa'}, tenho uma nova reserva pendente. Reserva: ${reservationRef.id}. Acesse a confirmação no sistema.`)}`;
        window.open(waLink, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Erro ao confirmar pedido:', error);
      setMessage(error?.message ? `Erro ao confirmar pedido: ${error.message}` : 'Erro ao confirmar pedido.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setMessage('Logout realizado.');
  };

  const navItems = [
    { id: 'dashboard', label: 'Início', icon: '⌂' },
    { id: 'profile', label: 'Perfil', icon: '◔' },
    { id: 'services', label: 'Serviços', icon: '☰' },
    { id: 'reservations', label: 'Reservas', icon: '✓' }
  ];

  const renderPageContent = () => {
    if (activePage === 'profile') {
      return (
        <section className="customer-page-card">
          <div className="customer-card__header">
            <div>
              <h2>Seu perfil</h2>
              <p>Complete seus dados para finalizar a reserva.</p>
            </div>
            <div className="customer-icon" style={{ background: company?.secondaryColor || '#7c3aed' }}>P</div>
          </div>
          <form className="customer-form" onSubmit={handleSaveProfile}>
            <input className="customer-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Nome completo" required />
            <input className="customer-input" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} placeholder="E-mail" required />
            <input className="customer-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="WhatsApp" required />
            <input className="customer-input" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="Endereço completo" required />
            <div className="customer-actions-row">
              <button className="customer-button customer-button--primary" style={{ background: company?.primaryColor || '#2563eb' }} type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Concluir perfil'}</button>
            </div>
          </form>
        </section>
      );
    }

    if (activePage === 'services') {
      return (
        <section className="customer-page-card">
            <div className="customer-card__header">
              <div>
                <h2>Buscar serviços</h2>
                <p>Escolha o serviço ideal para sua experiência.</p>
              </div>
              <div className="customer-icon" style={{ background: company?.primaryColor || '#2563eb' }}>S</div>
            </div>
            <input className="customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtre por nome ou descrição do serviço" />
            <div className="customer-filters">
              <label className="customer-filter-label" htmlFor="service-category">Categoria</label>
              <select id="service-category" className="customer-select" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                {serviceCategories.map(category => (
                  <option key={category} value={category}>{category === 'all' ? 'Todas' : category}</option>
                ))}
              </select>
            </div>
            {filteredServices.length === 0 ? (
              <div className="customer-empty-state">
                <div className="customer-empty-state__icon">✦</div>
                <h3>Nenhum serviço encontrado</h3>
                <p>Não há serviços disponíveis no momento para esta empresa.</p>
              </div>
            ) : (
              <div className="customer-service-cards">
                {filteredServices.map(service => {
                  const isSelected = selectedService?.id === service.id;
                  return (
                    <button
	                      key={service.id}
	                      type="button"
	                      className={`customer-service-card ${isSelected ? 'is-selected' : ''}`}
	                      onClick={() => selectServiceForBooking(service)}
	                    >
	                      <div className="customer-service-card__top">
	                        <span className="customer-service-card__type">{service.category || 'Serviço'}</span>
	                        <span className="customer-service-card__price">{formatCurrency(getServicePrice(service))}</span>
	                      </div>
	                      <h3>{service.name || service.title || 'Serviço'}</h3>
	                      <p>{service.description || 'Descrição em breve.'}</p>
	                      <div className="customer-service-card__footer">
	                        <span>{isSelected ? 'Abrindo pedido' : 'Selecionar e confirmar'}</span>
	                        <span>→</span>
	                      </div>
	                    </button>
	                  );
	                })}
	              </div>
	            )}
	          </section>
	      );
	    }

    if (activePage === 'booking') {
      if (!selectedService) {
        return (
          <section className="customer-page-card">
            <div className="customer-empty-state">
              <div className="customer-empty-state__icon">!</div>
              <h3>Selecione um serviço</h3>
              <p>Escolha um serviço antes de confirmar seu pedido.</p>
              <button className="customer-button customer-button--primary" style={{ background: company?.primaryColor || '#2563eb' }} onClick={() => setActivePage('services')} type="button">Ver serviços</button>
            </div>
          </section>
        );
      }

      const isPousada = isPousadaService(selectedService);

      return (
        <section className="customer-page-card customer-booking-page">
          <div className="customer-booking-header">
            <button className="customer-button customer-button--secondary" type="button" onClick={() => setActivePage('services')}>← Trocar serviço</button>
            <div className="customer-icon" style={{ background: company?.secondaryColor || '#7c3aed' }}>R</div>
          </div>

          <div className="customer-booking-layout">
            <aside className="customer-booking-summary">
              <span className="customer-service-card__type">{selectedService.category || 'Serviço'}</span>
              <h2>{selectedService.name || selectedService.title || 'Serviço'}</h2>
              <p>{selectedService.description || 'Descrição em breve.'}</p>
              <div className="customer-booking-price">{formatCurrency(getServicePrice(selectedService))}</div>
            </aside>

            <form className="customer-form customer-booking-form" onSubmit={(e) => { e.preventDefault(); handleReserve(selectedService); }}>
              <div className="customer-form-section">
                <h3>Seus dados</h3>
                <div className="customer-field-grid">
                  <label className="customer-field">
                    <span>Nome completo</span>
                    <input className="customer-input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required />
                  </label>
                  <label className="customer-field">
                    <span>E-mail</span>
                    <input className="customer-input" type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} required />
                  </label>
                  <label className="customer-field">
                    <span>WhatsApp</span>
                    <input className="customer-input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} required />
                  </label>
                  <label className="customer-field customer-field--full">
                    <span>Endereço completo</span>
                    <input className="customer-input" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} required />
                  </label>
                </div>
              </div>

              <div className="customer-form-section">
                <h3>Detalhes do pedido</h3>
                <div className="customer-field-grid">
                  {isPousada ? (
                    <>
                      <label className="customer-field">
                        <span>Check-in</span>
                        <input className="customer-input" type="date" value={reservationDetails.checkIn} onChange={(e) => setReservationDetails({ ...reservationDetails, checkIn: e.target.value })} required />
                      </label>
                      <label className="customer-field">
                        <span>Check-out</span>
                        <input className="customer-input" type="date" value={reservationDetails.checkOut} min={reservationDetails.checkIn} onChange={(e) => setReservationDetails({ ...reservationDetails, checkOut: e.target.value })} required />
                      </label>
                    </>
                  ) : (
                    <>
                      <label className="customer-field">
                        <span>Data desejada</span>
                        <input className="customer-input" type="date" value={reservationDetails.date} onChange={(e) => setReservationDetails({ ...reservationDetails, date: e.target.value })} required />
                      </label>
                      <label className="customer-field">
                        <span>Horário desejado</span>
                        <input className="customer-input" type="time" value={reservationDetails.time} onChange={(e) => setReservationDetails({ ...reservationDetails, time: e.target.value })} required />
                      </label>
                    </>
                  )}
                  <label className="customer-field">
                    <span>Quantidade de pessoas</span>
                    <input className="customer-input" type="number" min="1" value={reservationDetails.guests} onChange={(e) => setReservationDetails({ ...reservationDetails, guests: parseInt(e.target.value || '1', 10) })} required />
                  </label>
                  <label className="customer-field customer-field--full">
                    <span>Observações</span>
                    <textarea className="customer-input customer-textarea" rows="4" value={reservationDetails.notes} onChange={(e) => setReservationDetails({ ...reservationDetails, notes: e.target.value })} placeholder="Ex.: preferência por assento, alergias, chegada prevista..." />
                  </label>
                </div>
              </div>

              <div className="customer-booking-actions">
                <button className="customer-button customer-button--secondary" type="button" onClick={() => setActivePage('services')}>Voltar</button>
                <button className="customer-button customer-button--primary" style={{ background: company?.primaryColor || '#2563eb' }} type="submit" disabled={saving}>{saving ? 'Enviando...' : 'Confirmar pedido'}</button>
              </div>
            </form>
          </div>
        </section>
      );
    }

    if (activePage === 'reservations') {
      return (
        <section className="customer-page-card">
          <div className="customer-card__header">
            <div>
              <h2>Histórico</h2>
              <p>Veja suas reservas e o status atualizado.</p>
            </div>
            <div className="customer-icon" style={{ background: company?.secondaryColor || '#7c3aed' }}>H</div>
          </div>
          {reservations.length === 0 ? <p>Nenhuma reserva ainda.</p> : <div className="customer-list">{reservations.map(reservation => (
            <div key={reservation.id} className="customer-item">
              <div className="customer-item__title">
                <strong>{reservation.serviceName}</strong>
                <span className="customer-pill">{reservation.status}</span>
              </div>
              <p>Nº reserva: {reservation.id}</p>
              {isReviewable(reservation) && (
                <button className="customer-button customer-button--secondary" onClick={() => alert('Avaliação em breve')}>Avaliar serviço</button>
              )}
            </div>
          ))}</div>}
        </section>
      );
    }

    return (
      <section className="customer-page-card">
        <div className="customer-card__header">
          <div>
            <h2>Bem-vindo(a)</h2>
            <p>Seu espaço completo para reservar, acompanhar e revisar.</p>
          </div>
          <div className="customer-icon" style={{ background: company?.primaryColor || '#2563eb' }}>✦</div>
        </div>
        <div className="customer-stats-grid">
          <div className="customer-stat-card">
            <strong>{reservations.length}</strong>
            <span>Reservas</span>
          </div>
          <div className="customer-stat-card">
            <strong>{services.length}</strong>
            <span>Serviços</span>
          </div>
          <div className="customer-stat-card">
            <strong>{profile.name ? '1' : '0'}</strong>
            <span>Perfil pronto</span>
          </div>
        </div>
        <div className="customer-actions-row">
          <button className="customer-button customer-button--primary" style={{ background: company?.primaryColor || '#2563eb' }} onClick={() => setActivePage('services')}>Ver serviços</button>
          <button className="customer-button customer-button--secondary" onClick={() => setActivePage('reservations')}>Ver reservas</button>
        </div>
      </section>
    );
  };

  if (loading) return (
    <div className="customer-app">
      <LoadingState title="Carregando area do cliente" subtitle="Preparando servicos e reservas" />
    </div>
  );

  const heroStyle = {
    background: `linear-gradient(135deg, ${company?.primaryColor || '#2563eb'} 0%, ${company?.secondaryColor || '#7c3aed'} 100%)`
  };

  return (
    <div className="customer-app">
      <div className="customer-shell">
        <section className="customer-hero" style={heroStyle}>
          <div className="customer-hero__content">
            <div className="customer-brand">
              <div className="customer-brand__mark">
                {company?.logoUrl ? <img src={company.logoUrl} alt={company?.name || 'Logo'} /> : <span>✦</span>}
              </div>
              <div>
                <p className="customer-eyebrow">Experiência do cliente</p>
                <h1>{company?.name ? company.name : 'Área do cliente'}</h1>
                <p>Uma experiência organizada em páginas para reservar, acompanhar e gerir cada etapa com mais clareza.</p>
              </div>
            </div>
            <div className="customer-hero__pill">Reserva simples • WhatsApp • Acompanhamento</div>
          </div>
        </section>

        {message && <div className={`customer-message ${message.includes('Erro') || message.includes('erro') ? 'customer-message--error' : 'customer-message--success'}`}>{message}</div>}

        {!user ? (
          <section className="customer-card">
            <div className="customer-card__header">
              <div>
                <h2>Entrar com Google</h2>
                <p>Use sua conta Google para continuar.</p>
              </div>
              <div className="customer-icon" style={{ background: company?.primaryColor || '#2563eb' }}>G</div>
            </div>
            <p>Entre para completar seu perfil, reservar serviços e acompanhar seu histórico com segurança.</p>
            <button className="customer-button customer-button--primary" style={{ background: company?.primaryColor || '#2563eb' }} onClick={handleGoogleLogin}>Entrar com Google</button>
          </section>
        ) : (
          <div className="customer-dashboard-layout">
            <aside className="customer-sidebar">
              <div className="customer-sidebar__brand">Menu</div>
              {navItems.map(item => (
                <button
                  key={item.id}
                  className={`customer-sidebar__item ${activePage === item.id ? 'is-active' : ''}`}
                  onClick={() => setActivePage(item.id)}
                  type="button"
                >
                  <span>{item.icon}</span>
                  {item.label}
                </button>
              ))}
              <button className="customer-sidebar__item customer-sidebar__item--logout" onClick={handleLogout} type="button">↺ Sair</button>
            </aside>

            <div className="customer-content">
              <div className="customer-topbar" style={{ background: `linear-gradient(135deg, ${company?.primaryColor || '#2563eb'} 0%, ${company?.secondaryColor || '#7c3aed'} 100%)` }}>
                <div className="customer-topbar__brand">
                  <div className="customer-topbar__logo">
                    {company?.logoUrl ? <img src={company.logoUrl} alt={company?.name || 'Logo da empresa'} /> : <span>✦</span>}
                  </div>
                  <div>
                    <p className="customer-eyebrow customer-topbar__company-label">Empresa</p>
                    <h2>{company?.name || 'Área do cliente'}</h2>
                  </div>
                </div>
                <button className="customer-button customer-button--secondary customer-topbar__profile-button" onClick={() => setActivePage('profile')} type="button">Editar perfil</button>
              </div>
              {renderPageContent()}
            </div>
          </div>
        )}

        {user && <nav className="customer-mobile-nav">
          {navItems.map(item => (
            <button key={item.id} className={`customer-mobile-nav__item ${activePage === item.id ? 'is-active' : ''}`} onClick={() => setActivePage(item.id)} type="button">
              <span>{item.icon}</span>
              <small>{item.label}</small>
            </button>
          ))}
        </nav>}
      </div>
    </div>
  );
};

export default CustomerApp;
